import "twin.macro";

import { useState } from "react";
import { useSelector } from "react-redux";

import { CancelTwoTone, Edit } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";

import _ from "lodash";

import { OpaqueCard } from "@components/StyledComponents";
import { formatLongAddress } from "@features/addresses";
import { variantName } from "@features/items";
import { usePurchaseOrder } from "@features/purchaseOrders/context/PurchaseOrderContext";
import { useShippingParametersListQuery } from "@features/purchaseOrders/data/shippingParameterQueries";
import IndexTable, { Column } from "@features/ui/indexTable";
import { ShippingParameterVariant } from "@models/ShippingParameterVariant";
import { formatDateString, formatMoneyString } from "@utility/utilityFunctions";
import permissions from "@utils/permissions";
import useRoleIs from "@utils/useRoleIs";

import EditShippingParameterAddressModal from "./EditShippingParameterAddressModal";
import EditShippingParameterVariantModal from "./EditShippingParameterVariantModal";

const ShippingParameterTable = () => {
  const {
    purchaseOrder: { status, id },
  } = usePurchaseOrder();
  const { data: shippingParameters = [], ...tableProps } =
    useShippingParametersListQuery(id);

  const { usesItemCustomization, includesTaxes } = useSelector(
    (state: any) => state.currentUser.organization
  );

  const roleIs = useRoleIs();

  const readOnly = roleIs(permissions.supplier);
  const canEdit = !readOnly && status !== "complete";

  const [editAddress, setEditAddress] = useState({
    open: false,
    shippingParameter: null,
  });
  const [editSPV, setEditSPV] = useState({
    open: false,
    shippingParameterVariantId: null,
  });

  const handleEditAddressOpen = (sp) => {
    setEditAddress({
      open: true,
      shippingParameter: sp,
    });
  };

  const handleEditSPVOpen = (id) => {
    setEditSPV({
      open: true,
      shippingParameterVariantId: id,
    });
  };

  const handleCloseModal = () => {
    setEditAddress({
      open: false,
      shippingParameter: null,
    });
    setEditSPV({
      open: false,
      shippingParameterVariantId: null,
    });
  };

  return (
    <>
      {editAddress.open && editAddress.shippingParameter && (
        <EditShippingParameterAddressModal
          {...(editAddress as any)}
          handleClose={handleCloseModal}
        />
      )}
      {editSPV.open && editSPV.shippingParameterVariantId && (
        <EditShippingParameterVariantModal
          {...(editSPV as any)}
          purchaseOrderId={id}
          handleClose={handleCloseModal}
        />
      )}
      <IndexTable
        rows={shippingParameters}
        columns={[
          { id: "name", label: "Name" },
          {
            id: "_address",
            label: "Address",
            render: (_, sp) => formatLongAddress(sp),
          },
          { id: "attn", label: "Attention" },
          canEdit && {
            id: "_edit",
            label: "Edit",
            render: (_, sp) => (
              <Tooltip title="Edit Shipping Parmeter Address">
                <IconButton
                  size="small"
                  onClick={() => handleEditAddressOpen(sp)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            ),
          },
        ].filter((v) => !!v)}
        {...tableProps}
        collapsibleContent={(sp) => {
          return (
            <OpaqueCard tw="p-0">
              <IndexTable
                rows={sp.shippingParameterVariants}
                columns={
                  [
                    {
                      id: "_sku",
                      label: "External ID",
                      render: (_, spv: ShippingParameterVariant) =>
                        spv.variant.externalWarehouseId ??
                        spv.variant.variantSku,
                    },
                    {
                      id: "variant",
                      label: "Variant",
                      render: (v) => variantName(v),
                    },
                    usesItemCustomization && {
                      id: "customization",
                      label: "Customization",
                    },
                    { id: "qty", label: "Qty" },
                    { id: "method", label: "Method" },
                    { id: "trackingData.carrier", label: "Carrier" },
                    { id: "trackingData.number", label: "Tracking" },
                    includesTaxes && {
                      id: "tax",
                      label: "Tax",
                      align: "right",
                      render: formatMoneyString,
                    },
                    {
                      id: "actualShipDate",
                      label: "Ship Date",
                      render: formatDateString,
                    },
                    { id: "status", label: "Status", render: _.startCase },
                    canEdit && {
                      id: "_actions",
                      label: "Cancel",
                      render: (_, spv) => (
                        <Tooltip title="Cancel or Destroy Shipping Parmeter Item">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleEditSPVOpen(spv.id)}
                              disabled={spv.status !== "ok"}
                            >
                              <CancelTwoTone />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ),
                    },
                  ].filter((v) => !!v) as Column<ShippingParameterVariant>[]
                }
              />
            </OpaqueCard>
          );
        }}
      />
    </>
  );
};

export default ShippingParameterTable;
